import React, { useState, useEffect } from 'react';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './Config.css';

const GROUPS = [
  { key: 'general',  label: 'General Settings',  icon: '⚙️' },
  { key: 'payment',  label: 'Payment (Tuameka)',  icon: '💳' },
  { key: 'sms',      label: 'SMS (mNotify)',       icon: '📱' },
  { key: 'email',    label: 'Email (cPanel SMTP)', icon: '📧' },
];

export default function Config() {
  const { showToast } = useToast();
  const [activeGroup, setActiveGroup] = useState('general');
  const [settings,    setSettings]    = useState({});
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [showSecrets, setShowSecrets] = useState({});
  const [testTo, setTestTo]   = useState('');
  const [testing, setTesting] = useState(false);

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const r = await api.post('/admin/config/test-email', { to: testTo });
      showToast(r.data.message || 'Test email sent', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Test email failed', 'error');
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    api.get('/admin/config')
      .then(r => setSettings(r.data.settings || {}))
      .catch(() => showToast('Failed to load settings', 'error'))
      .finally(() => setLoading(false));
  }, [showToast]);

  const handleChange = (key, value) =>
    setSettings(s => ({ ...s, [key]: { ...s[key], value } }));

  const handleSave = async () => {
    setSaving(true);
    const updates = Object.entries(settings)
      .filter(([, v]) => v.group === activeGroup)
      .map(([key, v]) => ({ key, value: v.value }));
    try {
      await api.put('/admin/config', { settings: updates });
      showToast('Settings saved successfully', 'success');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const groupSettings = Object.entries(settings).filter(([, v]) => v.group === activeGroup);

  return (
    <Layout>
      <div className="config-page">
        <div className="page-header">
          <div>
            <h1>Configuration</h1>
            <p>Manage API keys, SMTP settings, and platform preferences.</p>
          </div>
        </div>

        <div className="config-layout">
          {/* Sidebar nav */}
          <div className="config-nav">
            {GROUPS.map(g => (
              <button
                key={g.key}
                className={`config-nav-btn ${activeGroup === g.key ? 'active' : ''}`}
                onClick={() => setActiveGroup(g.key)}
              >
                <span className="nav-icon">{g.icon}</span>
                <span>{g.label}</span>
              </button>
            ))}
          </div>

          {/* Settings panel */}
          <div className="config-panel">
            <div className="config-panel-header">
              <h2>{GROUPS.find(g => g.key === activeGroup)?.label}</h2>
            </div>

            {loading ? (
              <div className="config-loading"><div className="spinner" /></div>
            ) : (
              <>
                {groupSettings.length === 0 ? (
                  <div className="empty-state"><p>No settings in this group.</p></div>
                ) : (
                  <div className="settings-form">
                    {groupSettings.map(([key, setting]) => {
                      const isSensitive = setting.type === 'password';
                      const show = showSecrets[key];
                      return (
                        <div className="setting-row" key={key}>
                          <div className="setting-label-group">
                            <label className="setting-label">{setting.label || key}</label>
                            {setting.description && (
                              <p className="setting-desc">{setting.description}</p>
                            )}
                          </div>
                          <div className="setting-input-wrap">
                            {setting.type === 'textarea' ? (
                              <textarea
                                className="form-control"
                                rows={3}
                                value={setting.value || ''}
                                onChange={e => handleChange(key, e.target.value)}
                              />
                            ) : setting.type === 'select' ? (
                              <select
                                className="form-control"
                                value={setting.value || ''}
                                onChange={e => handleChange(key, e.target.value)}
                              >
                                {(setting.options || []).map(o => (
                                  <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                              </select>
                            ) : setting.type === 'toggle' ? (
                              <label className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={setting.value === '1' || setting.value === true}
                                  onChange={e => handleChange(key, e.target.checked ? '1' : '0')}
                                />
                                <span className="toggle-track" />
                                <span className="toggle-label">
                                  {(setting.value === '1' || setting.value === true) ? 'Enabled' : 'Disabled'}
                                </span>
                              </label>
                            ) : (
                              <div className="input-with-toggle">
                                <input
                                  type={isSensitive && !show ? 'password' : 'text'}
                                  className="form-control"
                                  value={setting.value || ''}
                                  onChange={e => handleChange(key, e.target.value)}
                                  placeholder={isSensitive ? '••••••••' : ''}
                                />
                                {isSensitive && (
                                  <button
                                    type="button"
                                    className="btn-eye"
                                    onClick={() => setShowSecrets(s => ({ ...s, [key]: !s[key] }))}
                                    title={show ? 'Hide' : 'Show'}
                                  >
                                    {show ? '🙈' : '👁️'}
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="config-footer">
                  {activeGroup === 'email' && (
                    <div className="config-test-email">
                      <input
                        type="email"
                        className="form-control"
                        placeholder="you@example.com"
                        value={testTo}
                        onChange={e => setTestTo(e.target.value)}
                      />
                      <button className="btn btn-outline" onClick={handleTestEmail} disabled={testing || !testTo}>
                        {testing ? <><span className="spinner spinner-sm" /> Sending…</> : '✉️ Send test email'}
                      </button>
                    </div>
                  )}
                  <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    {saving ? <><span className="spinner spinner-sm" /> Saving…</> : '💾 Save Settings'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
